/**
 * useCamera — manages getUserMedia video stream and frame capture.
 *
 * Captures JPEG frames from the camera at a configurable FPS (1–8).
 * Supports adaptive FPS: device-motion (Layer 1) + frame-similarity skip (Layer 2).
 * Uses async canvas.toBlob() instead of synchronous toDataURL for performance.
 * Persists each frame to IndexedDB immediately for crash resilience.
 */

import { useRef, useState, useCallback, useEffect } from 'react'
import { saveFrame as persistFrame, deleteFrame } from '@services/frameStore'
import { computeSimilarity } from '@utils/frameSimilarity'
import { computeSharpness } from '@utils/frameSharpness'
import type { PersistedFrame } from '@services/frameStore'
import type { ResolutionTier } from '@utils/scanSettings'

// ── Types ──

export interface UseCameraOptions {
  targetFps?: number     // Baseline FPS (1–8, default 2). Slider-controlled.
  quality?: number       // JPEG quality 0–1
  maxDimension?: number
  /** IndexedDB session ID for frame persistence. If not set, frames are not persisted. */
  scanSessionId?: string
  /** Enable adaptive FPS (device motion + similarity skip). Default false. */
  adaptiveFps?: boolean
  /** Minimum FPS when adaptive is on. Default 1. */
  minFps?: number
  /** Maximum FPS when adaptive is on. Default 8. */
  maxFps?: number
  /** Similarity threshold for frame skipping (0–1). Frames above this are skipped. Default 0.93. */
  similarityThreshold?: number
  /** Current device motion magnitude (0–1) from useDeviceMotion. Default 0.5. */
  motionMagnitude?: number
  /**
   * Laplacian variance threshold for blur rejection. Frames scoring below this are
   * considered blurry and skipped. Default 100. Calibrate with real scan frames.
   */
  blurThreshold?: number
  /** Resolution tier: 'standard' (HD 1080p) or 'ultra' (4K). Default 'standard'. */
  resolutionTier?: ResolutionTier
}

export interface CapturedFrame {
  blob: Blob
  hdBlob: Blob
  /** 4K blob, present only when resolutionTier is 'ultra' */
  ultraBlob?: Blob
  index: number
  timestamp: number
  /** Time taken to capture + encode this frame in ms */
  captureTimeMs?: number
}

export interface AdaptiveMetrics {
  /** Live effective FPS (may differ from targetFps when adaptive is on) */
  currentFps: number
  /** Total frames rejected by similarity check */
  framesSkipped: number
  /** Total frames rejected by blur detection */
  blurSkipped: number
  /** Rolling blur rejection rate over last 10 capture attempts (0–1) */
  blurRate: number
  /** True when >50% of recent frames are blurry — coach user to slow down */
  showSlowDownWarning: boolean
}

// ── Helpers ──

/** Wrap canvas.toBlob in a Promise. Much faster than toDataURL + atob. */
function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('canvas.toBlob returned null'))
      },
      type,
      quality,
    )
  })
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

// ── Hook ──

/** Number of recent capture attempts tracked for rolling blur rate. */
const BLUR_WINDOW_SIZE = 10
/** Blur rate above which the slow-down warning is shown. */
const BLUR_WARN_THRESHOLD = 0.5
/** Blur rate below which the warning is dismissed (hysteresis). */
const BLUR_WARN_DISMISS = 0.3

export function useCamera(options: UseCameraOptions = {}) {
  const {
    targetFps = 2,
    quality = 0.85,
    maxDimension = 800,
    scanSessionId,
    adaptiveFps = false,
    minFps = 1,
    maxFps = 8,
    similarityThreshold = 0.93,
    motionMagnitude = 0.5,
    blurThreshold = 100,
    resolutionTier = 'standard',
  } = options

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const hdCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const timeoutRef = useRef<number | null>(null)
  const frameIndexRef = useRef(0)
  const streamRef = useRef<MediaStream | null>(null)
  const capturingRef = useRef(false)              // Backpressure: prevent overlapping async captures
  const prevImageDataRef = useRef<ImageData | null>(null)
  const lastCapturedTimeRef = useRef(0)           // For force-capture after staleness
  const framesSkippedRef = useRef(0)
  const blurSkippedRef = useRef(0)
  const blurWindowRef = useRef<boolean[]>([])     // Rolling window of blur results (true = blurry)
  const showSlowDownRef = useRef(false)
  const currentFpsRef = useRef(targetFps)

  // Keep latest options in refs so self-scheduling loop reads fresh values
  const targetFpsRef = useRef(targetFps)
  const adaptiveFpsRef = useRef(adaptiveFps)
  const motionMagnitudeRef = useRef(motionMagnitude)
  const similarityThresholdRef = useRef(similarityThreshold)
  const minFpsRef = useRef(minFps)
  const maxFpsRef = useRef(maxFps)
  const qualityRef = useRef(quality)
  const maxDimensionRef = useRef(maxDimension)
  const scanSessionIdRef = useRef(scanSessionId)
  const blurThresholdRef = useRef(blurThreshold)
  const resolutionTierRef = useRef(resolutionTier)

  // Sync refs when options change
  useEffect(() => { targetFpsRef.current = targetFps }, [targetFps])
  useEffect(() => { adaptiveFpsRef.current = adaptiveFps }, [adaptiveFps])
  useEffect(() => { motionMagnitudeRef.current = motionMagnitude }, [motionMagnitude])
  useEffect(() => { similarityThresholdRef.current = similarityThreshold }, [similarityThreshold])
  useEffect(() => { minFpsRef.current = minFps }, [minFps])
  useEffect(() => { maxFpsRef.current = maxFps }, [maxFps])
  useEffect(() => { qualityRef.current = quality }, [quality])
  useEffect(() => { maxDimensionRef.current = maxDimension }, [maxDimension])
  useEffect(() => { scanSessionIdRef.current = scanSessionId }, [scanSessionId])
  useEffect(() => { blurThresholdRef.current = blurThreshold }, [blurThreshold])
  useEffect(() => { resolutionTierRef.current = resolutionTier }, [resolutionTier])

  const [isStreaming, setIsStreaming] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [frames, setFrames] = useState<CapturedFrame[]>([])
  const [error, setError] = useState<string | null>(null)
  const [adaptiveMetrics, setAdaptiveMetrics] = useState<AdaptiveMetrics>({
    currentFps: targetFps,
    framesSkipped: 0,
    blurSkipped: 0,
    blurRate: 0,
    showSlowDownWarning: false,
  })

  // Attach stream to video element when it becomes available (via ref)
  useEffect(() => {
    const video = videoRef.current
    const stream = streamRef.current
    if (!video || !stream || video.srcObject === stream) return

    video.srcObject = stream
    video.play()
      .then(() => setIsStreaming(true))
      .catch((err) => {
        console.error('Video play error:', err)
        setError('Could not play video stream')
      })
  })

  // Start camera stream
  const startCamera = useCallback(async () => {
    try {
      setError(null)

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError(
          'Camera API not available. Make sure you are using HTTPS. ' +
          'Open https://' + window.location.host + '/scan instead.'
        )
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: resolutionTier === 'ultra' ? 3840 : 1920 },
          height: { ideal: resolutionTier === 'ultra' ? 2160 : 1080 },
        },
        audio: false,
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
        setIsStreaming(true)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Camera access denied'
      setError(msg)
      console.error('Camera error:', err)
    }
  }, [])

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject = null
    }
    setIsStreaming(false)
  }, [])

  // Capture a single frame (async — uses canvas.toBlob)
  const captureFrame = useCallback(async (): Promise<CapturedFrame | null> => {
    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video || !canvas || video.readyState < 2) return null

    const t0 = performance.now()
    const vw = video.videoWidth
    const vh = video.videoHeight
    const curQuality = qualityRef.current
    const curMaxDim = maxDimensionRef.current

    // --- Low-res: scale to maxDimension for COLMAP/navigation ---
    const scale = Math.min(curMaxDim / Math.max(vw, vh), 1)
    const w = Math.round(vw * scale)
    const h = Math.round(vh * scale)

    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!
    ctx.drawImage(video, 0, 0, w, h)

    // --- Similarity check (before expensive encoding) ---
    if (adaptiveFpsRef.current) {
      const { similarity, currentImageData } = computeSimilarity(canvas, prevImageDataRef.current)
      const timeSinceLastCapture = Date.now() - lastCapturedTimeRef.current
      const isTooSimilar = similarity > similarityThresholdRef.current
      const isStale = timeSinceLastCapture > 3000 // Force-capture after 3s of skips

      if (isTooSimilar && !isStale) {
        // Skip this frame (too similar to previous)
        prevImageDataRef.current = currentImageData
        framesSkippedRef.current++
        setAdaptiveMetrics((prev) => ({
          ...prev,
          framesSkipped: framesSkippedRef.current,
        }))
        return null
      }
      prevImageDataRef.current = currentImageData

      // --- Blur detection (Layer 3): Laplacian variance on the drawn canvas ---
      const { isBlurry } = computeSharpness(canvas, blurThresholdRef.current)

      // Update rolling blur window
      const win = blurWindowRef.current
      win.push(isBlurry)
      if (win.length > BLUR_WINDOW_SIZE) win.shift()
      const blurCount = win.filter(Boolean).length
      const blurRate = win.length > 0 ? blurCount / win.length : 0

      // Hysteresis: show warning above WARN_THRESHOLD, dismiss below DISMISS threshold
      if (!showSlowDownRef.current && blurRate >= BLUR_WARN_THRESHOLD) {
        showSlowDownRef.current = true
      } else if (showSlowDownRef.current && blurRate < BLUR_WARN_DISMISS) {
        showSlowDownRef.current = false
      }

      if (isBlurry) {
        blurSkippedRef.current++
        setAdaptiveMetrics((prev) => ({
          ...prev,
          blurSkipped: blurSkippedRef.current,
          blurRate,
          showSlowDownWarning: showSlowDownRef.current,
        }))
        return null
      }

      // Frame passed all checks — update blur metrics (rate + warning state)
      setAdaptiveMetrics((prev) => ({
        ...prev,
        blurRate,
        showSlowDownWarning: showSlowDownRef.current,
      }))
    }

    // --- Encode low-res blob ---
    const blob = await canvasToBlob(canvas, 'image/jpeg', curQuality)

    // --- HD: full resolution for zoom display ---
    if (!hdCanvasRef.current) {
      hdCanvasRef.current = document.createElement('canvas')
    }
    const hdCanvas = hdCanvasRef.current
    hdCanvas.width = vw
    hdCanvas.height = vh
    const hdCtx = hdCanvas.getContext('2d')!
    hdCtx.drawImage(video, 0, 0, vw, vh)

    const hdBlob = await canvasToBlob(hdCanvas, 'image/jpeg', 0.90)

    // --- Ultra (4K): only when resolutionTier is 'ultra' and video is >1080p ---
    let ultraBlob: Blob | undefined
    if (resolutionTierRef.current === 'ultra' && (vw > 1920 || vh > 1080)) {
      // Video is already at 4K — HD canvas already has the full resolution
      // Ultra uses the same full-res canvas at higher quality
      ultraBlob = await canvasToBlob(hdCanvas, 'image/jpeg', 0.92)
    }

    const captureTimeMs = performance.now() - t0

    const frame: CapturedFrame = {
      blob,
      hdBlob,
      ultraBlob,
      index: ++frameIndexRef.current,
      timestamp: Date.now(),
      captureTimeMs,
    }
    lastCapturedTimeRef.current = frame.timestamp
    return frame
  }, []) // All mutable values read from refs — no deps needed

  // Compute effective FPS from motion when adaptive is on.
  // INVERTED logic: high motion → lower FPS to reduce blur at source.
  // motion=0 (still)  → maxFps  (capture more when not moving)
  // motion=0.5        → targetFps (baseline)
  // motion=1 (moving) → minFps  (capture less during fast movement)
  const computeEffectiveFps = useCallback((): number => {
    if (!adaptiveFpsRef.current) {
      return targetFpsRef.current
    }
    const motion = motionMagnitudeRef.current
    const base = targetFpsRef.current
    const lo = minFpsRef.current
    const hi = maxFpsRef.current

    let fps: number
    if (motion < 0.5) {
      // Still → faster: interpolate between maxFps and targetFps
      fps = hi - (hi - base) * (motion / 0.5)
    } else {
      // Moving → slower: interpolate between targetFps and minFps
      fps = base - (base - lo) * ((motion - 0.5) / 0.5)
    }
    return clamp(fps, lo, hi)
  }, [])

  // Self-scheduling capture loop using setTimeout
  const scheduleNextCapture = useCallback(() => {
    const effectiveFps = computeEffectiveFps()
    currentFpsRef.current = effectiveFps
    setAdaptiveMetrics((prev) => ({ ...prev, currentFps: Math.round(effectiveFps * 10) / 10 }))

    const intervalMs = 1000 / effectiveFps

    timeoutRef.current = window.setTimeout(async () => {
      // Backpressure: skip if previous frame is still processing
      if (capturingRef.current) {
        scheduleNextCapture()
        return
      }

      capturingRef.current = true
      try {
        const frame = await captureFrame()
        if (frame) {
          setFrames((prev) => [...prev, frame])

          // Persist to IndexedDB if session is active (fire-and-forget)
          const sessId = scanSessionIdRef.current
          if (sessId) {
            const persistedFrame: PersistedFrame = {
              id: `${sessId}_${frame.index}`,
              scanSessionId: sessId,
              frameIndex: frame.index,
              blob: frame.blob,
              hdBlob: frame.hdBlob,
              ...(frame.ultraBlob ? { ultraBlob: frame.ultraBlob } : {}),
              status: 'pending',
              createdAt: frame.timestamp,
            }
            persistFrame(persistedFrame).catch((err) =>
              console.warn('[useCamera] Failed to persist frame to IndexedDB:', err)
            )
          }
        }
      } catch (err) {
        console.warn('[useCamera] Frame capture error:', err)
      } finally {
        capturingRef.current = false
      }

      // Schedule next tick
      scheduleNextCapture()
    }, intervalMs)
  }, [captureFrame, computeEffectiveFps])

  // Start auto-capture
  const startCapture = useCallback(() => {
    if (!isStreaming || isCapturing) return

    frameIndexRef.current = 0
    framesSkippedRef.current = 0
    blurSkippedRef.current = 0
    blurWindowRef.current = []
    showSlowDownRef.current = false
    prevImageDataRef.current = null
    lastCapturedTimeRef.current = Date.now()
    setFrames([])
    setAdaptiveMetrics({ currentFps: targetFps, framesSkipped: 0, blurSkipped: 0, blurRate: 0, showSlowDownWarning: false })
    setIsCapturing(true)

    // Start self-scheduling loop
    scheduleNextCapture()
  }, [isStreaming, isCapturing, targetFps, scheduleNextCapture])

  // Stop auto-capture
  const stopCapture = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    capturingRef.current = false
    setIsCapturing(false)
  }, [])

  // Remove a single frame by index
  const removeFrame = useCallback((index: number) => {
    setFrames((prev) => {
      const removed = prev[index]
      if (scanSessionId && removed) {
        deleteFrame(`${scanSessionId}_${removed.index}`).catch(() => {})
      }
      return prev.filter((_, i) => i !== index)
    })
  }, [scanSessionId])

  // Clear captured frames
  const clearFrames = useCallback(() => {
    setFrames([])
    frameIndexRef.current = 0
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCapture()
      stopCamera()
    }
  }, [stopCapture, stopCamera])

  return {
    videoRef,
    canvasRef,
    isStreaming,
    isCapturing,
    frames,
    error,
    startCamera,
    stopCamera,
    startCapture,
    stopCapture,
    removeFrame,
    clearFrames,
    /** Adaptive FPS metrics (currentFps, framesSkipped) */
    adaptiveMetrics,
  }
}
